/**
 * VendorLedgerPage.jsx — vendor's Sales + Payouts dashboard.
 *
 * The vendor lands here from their admin console. Reads:
 *   GET /api/ledger/vendor/{vendor_id}
 *
 * Groups transactions by state (held / released / paid_out / etc.)
 * and shows aggregated $ per bucket so the vendor can see exactly
 * what's owed to them today and what's been paid out.
 *
 * The vendor_id is taken from the URL param — the API enforces that
 * the caller must own the vendor (or be an admin) via
 * `owned_vendor_id` on the user session.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wallet, ArrowRight, CheckCircle2, RefreshCw, Gift, XCircle } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function fmtMoney(cents = 0, cur = "AUD") {
  return `$${(cents / 100).toFixed(2)} ${cur}`;
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  }); } catch { return iso; }
}

const BUCKETS = [
  ["held",                       "Held (in Stripe balance)", Wallet,        "text-amber-500",   "bg-amber-500/10"],
  ["released",                   "Released (Transfer sent)", ArrowRight,    "text-sky-500",     "bg-sky-500/10"],
  ["paid_out",                   "Paid to your bank",        CheckCircle2,  "text-emerald-500", "bg-emerald-500/10"],
  ["refunded_to_buyer",          "Refunded to buyer",        RefreshCw,     "text-red-500",     "bg-red-500/10"],
  ["credit_to_buyer_at_vendor",  "Credit issued to buyer",   Gift,          "text-violet-500",  "bg-violet-500/10"],
  ["disputed",                   "Disputed / chargeback",    XCircle,       "text-rose-500",    "bg-rose-500/10"],
];

const AMOUNT_KEY = {
  held:                      "held_cents",
  released:                  "released_cents",
  paid_out:                  "paid_out_cents",
  refunded_to_buyer:         "refunded_cents",
  credit_to_buyer_at_vendor: "credit_outstanding_cents",
  disputed:                  null,
};

export default function VendorLedgerPage() {
  const { vendor_id } = useParams();
  const { t, i18n } = useTranslation();
  const [data,  setData]  = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = "Vendor ledger — HustleSuite";
    (async () => {
      try {
        const { data: d } = await axios.get(
          `${API}/ledger/vendor/${vendor_id}`,
          { headers: authHeader(), withCredentials: true });
        setData(d);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    })();
  }, [vendor_id, i18n.language]);

  return (
    <div
      data-testid="vendor-ledger-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleSuite · Vendor", to: "/vendor" }}
        showBookCta={false}
        showCart={false}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <header className="mb-8">
          <p className="text-amber-500 text-[11px] font-bold uppercase tracking-[0.32em]">
            {t("vendor_ledger_kicker", { defaultValue: "Vendor ledger" })}
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-black leading-tight">
            {t("vendor_ledger_title", { defaultValue: "Sales & payouts" })}
          </h1>
          <p className="mt-2 text-sm text-stone-500 dark:text-stone-400">
            {t("vendor_ledger_sub", {
              defaultValue:
                "Every dollar HustleSuite is holding for you or has already paid out. Money moves to your Stripe account the moment the service is confirmed as delivered.",
            })}
          </p>
        </header>

        {error && (
          <div className="rounded-xl border border-red-300 bg-red-50 text-red-800 px-4 py-3 mb-6"
            data-testid="vendor-ledger-error">{error}</div>
        )}

        {/* Summary buckets */}
        {data && (
          <div data-testid="vendor-buckets"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {BUCKETS.map(([k, label, Icon, colour, bg]) => {
              const key = AMOUNT_KEY[k];
              const amt = key ? (data.summary?.[key] || 0) : 0;
              return (
                <div key={k}
                  data-testid={`bucket-${k}`}
                  className={`rounded-xl border border-stone-200 dark:border-stone-800 ${bg} p-5`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={16} className={colour}/>
                    <p className="text-[11px] uppercase tracking-widest text-stone-500 font-bold">{label}</p>
                  </div>
                  <p className={`mt-3 text-2xl font-black ${colour}`}>{fmtMoney(amt)}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Transactions */}
        <h2 className="text-lg font-black mb-3">
          {t("vendor_ledger_recent", { defaultValue: "Recent transactions" })}
        </h2>
        {data === null && !error && <p className="text-stone-400">Loading…</p>}
        {data && data.transactions?.length === 0 && (
          <p className="text-stone-400">{t("vendor_ledger_empty",
            { defaultValue: "No transactions yet." })}</p>
        )}
        {data && data.transactions?.map((tx) => (
          <div key={tx.entry_id}
            data-testid={`vendor-tx-${tx.entry_id}`}
            className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-5 mb-3 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-4 items-center"
          >
            <div className="min-w-0">
              <p className="font-bold truncate">{tx.item_label}</p>
              <p className="text-xs text-stone-500 mt-1">
                {tx.buyer_email || tx.buyer_id?.slice(-6)} · {fmtDate(tx.created_at)}
              </p>
              <p className="text-[10px] mt-1 text-stone-400 font-mono">
                {tx.entry_id}
              </p>
            </div>
            <div className="text-sm">
              <p><span className="text-stone-400">Gross:</span> {fmtMoney(tx.gross_cents, tx.currency)}</p>
              <p><span className="text-stone-400">Net to you:</span> <span className="font-bold">{fmtMoney(tx.net_to_vendor_cents, tx.currency)}</span></p>
              <p className="text-[11px] text-stone-400">Fee: {fmtMoney(tx.platform_fee_cents)}</p>
            </div>
            <div className="text-right">
              <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                tx.state === "paid_out" ? "bg-emerald-500/10 text-emerald-500" :
                tx.state === "held"     ? "bg-amber-500/10 text-amber-500" :
                tx.state === "released" ? "bg-sky-500/10 text-sky-500" :
                "bg-stone-500/10 text-stone-500"
              }`}>
                {tx.state.replace(/_/g, " ")}
              </span>
            </div>
          </div>
        ))}
      </div>
      <SuiteFooter />
    </div>
  );
}
